import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import convertImageSourcesToPdf from 'views/EMR/components/WebcamScanDialog/utils';

export type ParseEOBSource = 'pdf' | 'images' | 'capture';
export type PendingEOBSelectionKind = 'pdf' | 'images' | null;

export type PendingEOBSelection = {
  source: ParseEOBSource | null;
  files: FileLocalUrl[];
  kind: PendingEOBSelectionKind;
  error: string | null;
  summaryLabel: string;
};

const IMAGE_EXTENSION_REGEXP = /\.(png|jpe?g|tiff)$/i;
const PDF_EXTENSION_REGEXP = /\.pdf$/i;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function getEmptySelection(source: ParseEOBSource | null): PendingEOBSelection {
  return {
    source,
    files: [],
    kind: null,
    error: null,
    summaryLabel: '',
  };
}

export function isPdfFile(file: File): boolean {
  return file.type.includes('pdf') || PDF_EXTENSION_REGEXP.test(file.name);
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || IMAGE_EXTENSION_REGEXP.test(file.name);
}

function getImageSummaryLabel(imageFiles: FileLocalUrl[]): string {
  if (imageFiles.length === 1) {
    return imageFiles[0]?.file?.name ?? '1 image selected';
  }

  return `${imageFiles.length} images selected`;
}

export function shouldPreserveImageSelection(
  currentSource: ParseEOBSource | null,
  nextSource: ParseEOBSource,
): boolean {
  const imageSources: ParseEOBSource[] = ['images', 'capture'];
  return imageSources.includes(currentSource as ParseEOBSource)
    && imageSources.includes(nextSource);
}

export async function validateUploadedEOBFiles(
  files: File[],
  source: Exclude<ParseEOBSource, 'capture'>,
): Promise<PendingEOBSelection> {
  if ((files ?? []).length === 0) {
    return getEmptySelection(source);
  }

  const pdfFiles = files.filter(isPdfFile);
  const imageFiles = files.filter(isImageFile);
  const hasUnsupportedFiles = pdfFiles.length + imageFiles.length !== files.length;

  if (pdfFiles.length > 1) {
    return {
      ...getEmptySelection(source),
      error: 'Only one PDF file is allowed.',
    };
  }

  if (pdfFiles.length > 0 && imageFiles.length > 0) {
    return {
      ...getEmptySelection(source),
      error: 'Please upload either one PDF or one or more images, not both together.',
    };
  }

  if (source === 'pdf' && (imageFiles.length > 0 || hasUnsupportedFiles)) {
    return {
      ...getEmptySelection(source),
      error: 'Only one PDF file is allowed.',
    };
  }

  if (source === 'images' && (pdfFiles.length > 0 || hasUnsupportedFiles)) {
    return {
      ...getEmptySelection(source),
      error: 'Please upload image files only.',
    };
  }

  if (pdfFiles.length === 1) {
    const [pdfFile] = pdfFiles;
    return {
      source,
      files: [{ file: pdfFile }],
      kind: 'pdf',
      error: null,
      summaryLabel: pdfFile.name,
    };
  }

  if (imageFiles.length > 0) {
    const localFiles = await Promise.all(imageFiles.map(async (file) => ({
      file,
      dataUrl: await fileToDataUrl(file),
    })));

    return {
      source,
      files: localFiles,
      kind: 'images',
      error: null,
      summaryLabel: getImageSummaryLabel(localFiles),
    };
  }

  return {
    ...getEmptySelection(source),
    error: 'Please upload a supported PDF or image file.',
  };
}

export function mergeImageSelections(
  current: PendingEOBSelection,
  incomingFiles: FileLocalUrl[],
  source: ParseEOBSource,
): PendingEOBSelection {
  const nextFiles = [
    ...(current.kind === 'images' ? current.files : []),
    ...incomingFiles.filter((item) => item.file && isImageFile(item.file)),
  ];

  if (nextFiles.length === 0) {
    return getEmptySelection(source);
  }

  return {
    source,
    files: nextFiles,
    kind: 'images',
    error: null,
    summaryLabel: getImageSummaryLabel(nextFiles),
  };
}

export async function buildGeneratedPdf(
  selection: PendingEOBSelection,
): Promise<FileLocalUrl | null> {
  if (selection.kind !== 'images' || selection.files.length <= 1) {
    return null;
  }

  const imageSources = await Promise.all(selection.files.map(async (item) => {
    if (item.dataUrl) {
      return item.dataUrl;
    }
    if (item.file) {
      return fileToDataUrl(item.file);
    }
    return '';
  }));

  return convertImageSourcesToPdf(imageSources.filter(Boolean));
}

export async function resolveEOBExtractionFile(
  selection: PendingEOBSelection,
  generatedPdfCache?: FileLocalUrl | null,
): Promise<File | null> {
  if (selection.kind === 'pdf') {
    const pdfFile = selection.files[0]?.file ?? null;
    // PDF stays as PDF.
    return pdfFile;
  }

  if (selection.kind !== 'images') {
    return null;
  }

  const imageFiles = selection.files
    .map((item) => item.file)
    .filter((file): file is File => Boolean(file));

  if (imageFiles.length === 0) {
    return null;
  }

  if (imageFiles.length === 1) {
    // A single image stays as an image.
    return imageFiles[0];
  }

  if (generatedPdfCache?.file) {
    // Multiple images reuse the same generated PDF artifact once it exists.
    return generatedPdfCache.file;
  }

  // Multiple images are combined into a single PDF payload.
  const generatedPdf = await buildGeneratedPdf(selection);
  return generatedPdf?.file ?? null;
}

export async function buildEOBExtractionFile(selection: PendingEOBSelection): Promise<File | null> {
  return resolveEOBExtractionFile(selection);
}

export function formatElapsedSeconds(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
